import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';

import type { GameplayTier } from '@sorare/core/src/__generated__/globalTypes';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { GameplayTierStars } from '@sorare/core/src/components/card/GameplayTierStars';
import { fromBirthDateToAge } from '@sorare/core/src/lib/birthDate';
import { FILTERS, FLOOR_PRICES_FILTERS } from '@sorare/core/src/lib/filters';
import type { AvailableLanguage } from '@sorare/core/src/lib/i18n.ts';
import { formatPosition } from '@sorare/core/src/lib/players';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import { formatPlayingStatus } from '#src/lib/playingStatus.ts';
import type { RefinementItem } from '#src/search/ActiveFilters/types.ts';
import { formatBundledOption } from '#src/searchCards/RefineList/index.tsx';
import { getFormatSettlementWallets } from '#src/searchCards/RefineSettlementWallets/index.tsx';

const I18N_FILTERS = Object.values(FILTERS).filter(
  v => 'i18nAttribute' in v
) as {
  i18nAttribute: (language: AvailableLanguage) => string;
}[];

export const formatListFilterWidgetValue = (
  item: RefinementItem,
  options: {
    formatMessage: (m: MessageDescriptor) => string;
    language: AvailableLanguage;
  }
): ReactNode[] => {
  return item.refinements.map(refinement => {
    // eslint-disable-next-line prefer-destructuring
    let label: ReactNode = refinement.label;
    if (
      item.attribute === FILTERS.rarity.attribute &&
      Object.keys(Rarity).includes(refinement.value.toString())
    ) {
      const rarity = refinement.value as Rarity;
      label = options.formatMessage(
        scarcityMessages[rarity] as MessageDescriptor
      );
    } else if (item.attribute === FILTERS.bundledSale.attribute) {
      label = formatBundledOption(
        refinement.value.toString(),
        options.formatMessage
      );
    } else if (item.attribute === FILTERS.position.attribute) {
      label = formatPosition(
        refinement.value.toString(),
        options.formatMessage
      );
    } else if (item.attribute === FILTERS.gameplayTier.attribute) {
      label = (
        <GameplayTierStars gameplayTier={refinement.value as GameplayTier} />
      );
    } else if (item.attribute === FILTERS.settlementWallets.attribute) {
      const formatSettlementWallets = getFormatSettlementWallets(true);
      label = formatSettlementWallets(
        refinement.value.toString(),
        options.formatMessage
      );
    } else if (item.attribute === FILTERS.playingStatus.attribute) {
      label = formatPlayingStatus(refinement.label, options.formatMessage);
    } else if (
      I18N_FILTERS.find(
        attr => attr.i18nAttribute(options.language) === item.attribute
      )
    ) {
      label = String(refinement.value).split('|').at(0)!;
    }

    return label;
  });
};

export const formatRangeFilterWidgetValue = (
  item: RefinementItem,
  options: {
    formatPrice: (value: number) => string;
    formatFloorPrices: (value: number) => string;
  }
): string[] => {
  let sortedRefinements = item.refinements;
  if (item.attribute === FILTERS.age.attribute) {
    sortedRefinements = item.refinements.sort(
      (a, b) => Number(b.value) - Number(a.value)
    );
  }

  const refinements = sortedRefinements.map(refinement => {
    const numberValue = Number(refinement.value);

    let formattedValue;
    switch (item.attribute) {
      case FILTERS.age.attribute:
        formattedValue = fromBirthDateToAge(numberValue);
        break;
      case FILTERS.price.attribute:
        formattedValue = options.formatPrice(numberValue);
        break;

      case FLOOR_PRICES_FILTERS.allSeasonsLimited.attribute:
      case FLOOR_PRICES_FILTERS.allSeasonsRare.attribute:
      case FLOOR_PRICES_FILTERS.allSeasonsSuperRare.attribute:
      case FLOOR_PRICES_FILTERS.allSeasonsUnique.attribute:
      case FLOOR_PRICES_FILTERS.inSeasonLimited.attribute:
      case FLOOR_PRICES_FILTERS.inSeasonRare.attribute:
      case FLOOR_PRICES_FILTERS.inSeasonSuperRare.attribute:
      case FLOOR_PRICES_FILTERS.inSeasonUnique.attribute:
        formattedValue = options.formatFloorPrices(numberValue);
        break;
      case FILTERS.lastFiveAppearances.attribute:
        formattedValue = `${Math.round(numberValue * (100 / 5))}%`;
        break;
      case FILTERS.lastFifteenAppearances.attribute:
        formattedValue = `${Math.round(numberValue * (100 / 15))}%`;
        break;
      default:
        formattedValue = numberValue;
        break;
    }

    return {
      ...refinement,
      value: formattedValue,
    };
  });

  let text;
  if (refinements.length === 1) {
    const [{ value, operator: rawOperator }] = refinements;

    const operators: { [key: string]: string } = {
      '>=': '≥',
      '<=': '≤',
    };
    let operator = operators[rawOperator];
    if (item.attribute === FILTERS.age.attribute) {
      const reverseRawOperator = rawOperator === '>=' ? '<=' : '>=';
      operator = operators[reverseRawOperator];
    }
    text = `${operator} ${value}`;
  } else {
    text = `${refinements[0].value} - ${refinements[1].value}`;
  }

  return [text];
};
